import React, { useEffect, useState } from "react";
import { FaSearch, FaCaretDown, FaShoppingCart } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { getCart } from "../../api/cartApi";
import axios from "axios";
import useSponsoredAds from "../../hook/useSponsoredAds";
const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
const TopSearch = () => {
  const [cartCount, setCartCount] = useState(0);
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  // Debounced term for the "search" ad placement - the API requires at least one letter/number.
  const [adSearchTerm, setAdSearchTerm] = useState("");
  const navigate = useNavigate();
  const { ads: sponsoredAds, trackEvent, viewRef } = useSponsoredAds({
    placement: "search",
    context: { search: adSearchTerm },
    count: 2,
    enabled: /[\p{L}\p{N}]/u.test(adSearchTerm),
  });

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        const res = await getCart();

        // ✅ Handle both possible API response shapes
        const count =
          res?.result?.total_items ??
          res?.total_items ??
          (Array.isArray(res?.result?.cart_items)
            ? res.result.cart_items.length
            : 0);

        if (mounted) setCartCount(count || 0);
      } catch (err) {
        console.error("Failed to fetch cart count:", err);
        if (mounted) setCartCount(0);
      }
    }

    load();
    return () => (mounted = false);
  }, []);

  // ✅ Debounced Search Function
  useEffect(() => {
    if (!query.trim()) {
      setSuggestions([]);
      setShowSuggestions(false);
      setAdSearchTerm("");
      return;
    }

    const delayDebounce = setTimeout(() => {
      fetchSuggestions(query);
      setAdSearchTerm(query.trim().slice(0, 500));
    }, 500); // debounce: wait 500ms after user stops typing

    return () => clearTimeout(delayDebounce);
  }, [query]);

  const fetchSuggestions = async (searchTerm) => {
    try {
      setLoading(true);
      const token =
        sessionStorage.getItem("accessToken") ||
        localStorage.getItem("accessToken");

      if (!token) {
        console.warn("No authentication token found");
        return;
      }

      const res = await axios.post(
        `${API_BASE_URL}/product/get-products`,
        {
          filters: { search: searchTerm },
          page: 1,
          limit: 10,
        },
        {
          headers: {
            token,
            "Content-Type": "application/json",
          },
        }
      );

      if (res?.data?.success && Array.isArray(res.data.result?.products)) {
        // Store complete product objects
        setSuggestions(res.data.result.products);
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
        // Keep the dropdown open - a sponsored result can still match with no organic ones.
        setShowSuggestions(true);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = async (product) => {
    try {
        // Validate product has required data
      if (!product || !product.id) {
        console.error("Product missing required data:", product);
        return;
      }
      setQuery(product.product_name);
      setShowSuggestions(false);
 setSuggestions([]);
 
      // Navigate with state containing full product data
 // Pass complete product data via state
      navigate(`/ProductView/${product.id}`, {
        state: { product: product },
        replace: false
      });
        } catch (error) {
      console.error("Error selecting product:", error);
    }
  };

  return (
    <div className="flex flex-wrap justify-between items-center gap-3 relative">
      {/* Left: Search */}
      <div className="flex items-center gap-2 w-full max-w-md relative">
        <div className="relative flex-1">
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => query && setShowSuggestions(true)}
            placeholder="Search products"
            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-green-500"
          />

          {/* Update the suggestions list to use correct property names */}
         {/* Suggestions Dropdown */}
          {showSuggestions && (suggestions.length > 0 || sponsoredAds.length > 0) && (
            <ul className="absolute z-10 bg-white border border-gray-200 w-full mt-1 rounded-lg shadow-lg max-h-96 overflow-y-auto">
              {loading ? (
                <li className="p-2 text-gray-500 text-sm">Loading...</li>
              ) : (
                <>
                {sponsoredAds.map((ad) => (
                  <li
                    key={ad.trackingId}
                    ref={viewRef(ad)}
                    className="px-4 py-2 bg-[#FEFDEB] hover:bg-yellow-50 cursor-pointer border-b transition"
                    onClick={() => {
                      trackEvent(ad);
                      setShowSuggestions(false);
                      navigate(`/ProductView/${ad.product.id}`);
                    }}
                  >
                    <div className="flex items-center space-x-3">
                      <img
                        src={ad.creative_media?.[0]?.url || ad.product.product_media?.[0]?.url || "https://via.placeholder.com/48"}
                        alt={ad.product.product_name}
                        className="w-12 h-12 object-cover rounded"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{ad.product.product_name}</p>
                        <p className="text-sm text-green-600">
                          ${parseFloat(ad.product.discount_price || ad.product.product_price).toFixed(2)}
                        </p>
                      </div>
                      <span className="shrink-0 bg-[#F97316] text-white text-[10px] font-bold px-2 py-0.5 rounded-md">
                        Sponsored
                      </span>
                    </div>
                  </li>
                ))}
                {suggestions.map((product) => (
                  <li
                    key={product.id}
                    className="px-4 py-2 hover:bg-gray-50 cursor-pointer border-b transition"
                    onClick={() => handleSelect(product)}
                  >
                    <div className="flex items-center space-x-3">
                      {product.product_media?.[0]?.url ? (
                        <img
                          src={product.product_media[0].url}
                          alt={product.product_name}
                          className="w-12 h-12 object-cover rounded"
                          onError={(e) => {
                            e.target.src = "https://via.placeholder.com/48";
                          }}
                        />
                      ) : (
                        <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
                          <FaSearch className="text-gray-400" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">
                          {product.product_name}
                        </p>
                        <p className="text-sm text-green-600">
                          ${parseFloat(product.discount_price || product.product_price).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </li>
                ))}
                </>
              )}
            </ul>
          )}
        </div>
      </div>

      {/* Right: Profile & Cart */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate("/myprofile")}
          className="px-4 py-2 whitespace-nowrap bg-green-700 text-white rounded-full hover:bg-green-800 transition"
        >
          My Profile
        </button>

        <button
          onClick={() => navigate("/cart")}
          className="relative px-4 py-2 whitespace-nowrap bg-green-700 text-white rounded-full hover:bg-green-800 transition flex items-center gap-2"
        >
          <FaShoppingCart />
          View Cart
          {cartCount > 0 && (
            <span className="absolute -top-2 -right-2 w-5 h-5 text-xs flex items-center justify-center text-white bg-yellow-400 rounded-full">
              {cartCount}
            </span>
          )}
        </button>
      </div>
    </div>
  );
};

export default TopSearch;
